import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IDepartmentState } from "./types";
import { IIDAStore } from "../types";
import { departmentService } from "../../services/department.service";
import {
  Department,
  DepartmentEmployee,
  IDepartment,
  IDepartmentPost
} from "../../models/department";

export const getInitialState = (): IDepartmentState => {
  return {
    departments: [],
    departmentEmployees: []
  };
};

const namespaced = true;

export const getters: GetterTree<IDepartmentState, IIDAStore> = {
  GetDepartmentEmployeesByDepartmentId: (state) => (departmentId: number) => {
    const list = state.departmentEmployees.filter((a) => a.id === departmentId);
    return list;
  },
  GetActiveDepartments: (state) => () => {
    return state.departments.filter((d) => d.is_active === true);
  },
  IsEmployeeMemberOfDepartment: (state) => (employee_id: number, department_id: number) => {
    return state.departmentEmployees.find(
      (d) => d.department_id == department_id && d.employee_id == employee_id
    );
  },
  GetEmployeesInDepartments:
    (state) =>
    (departmentIds: number[]): number[] => {
      const EmployeesID: number[] = state.departmentEmployees
        .filter((de) => departmentIds.includes(de.department_id))
        .map((d) => d.employee_id);
      return Array.from(new Set(EmployeesID));
    },
  GetDepartmentsByEmployeeId:
    (state) =>
    (employeeId: number): Department[] => {
      const departments: Department[] = state.departmentEmployees
        .filter((de) => de.employee_id == employeeId)
        .map((d) => state.departments.find((de) => de.id == d.department_id)!);
      return departments;
    }
};

export const actions: ActionTree<IDepartmentState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  deleteDepartment({ commit }, departmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      departmentService
        .deleteDepartments(departmentId)
        .then((response) => {
          commit("deleteDepartment", departmentId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createDepartment({ commit }, department: IDepartmentPost): Promise<IDepartment> {
    return new Promise((resolve, reject) => {
      departmentService
        .createDepartments(department)
        .then((newDepartment: Department) => {
          commit("updateDepartments", { departments: [newDepartment] });
          resolve(newDepartment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateDepartment({ commit }, department: Department): Promise<Department> {
    return new Promise((resolve, reject) => {
      departmentService
        .updateDepartments(department)
        .then((newDepartment: Department) => {
          commit("updateDepartments", { departments: [newDepartment] });
          resolve(newDepartment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchDepartments({ state, commit }, update = false): Promise<Department[]> {
    return new Promise((resolve, reject) => {
      try {
        if (state.departments.length === 0 || update) {
          departmentService.fetchDepartments().then((departments: Department[]) => {
            commit("updateDepartments", { departments: departments });
            return resolve(departments);
          });
        } else {
          resolve(state.departments);
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  /**
   *  Department Employees
   *  */

  fetchDepartmentEmployees({ state, commit }, update = false): Promise<DepartmentEmployee[]> {
    return new Promise((resolve, reject) => {
      try {
        if (state.departmentEmployees.length === 0 || update) {
          departmentService
            .fetchDepartmentEmployees()
            .then((departmentEmployees: DepartmentEmployee[]) => {
              commit("updateDepartmentEmployees", { departmentEmployees: departmentEmployees });
              return resolve(departmentEmployees);
            });
        } else {
          resolve(state.departmentEmployees);
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  SaveDepartmentEmployees(
    { dispatch, state },
    payload: { departmentId: number; employeeIds: number[] }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const currentEmployees: DepartmentEmployee[] = state.departmentEmployees.filter(
          (de) => de.department_id == payload.departmentId
        );
        const promiseArray: Promise<DepartmentEmployee>[] = [];
        // Find employees to create
        for (let index = 0; index < payload.employeeIds.length; index++) {
          const id = payload.employeeIds[index];
          if (!currentEmployees.find((ce) => ce.employee_id == id)) {
            promiseArray.push(
              dispatch("createDepartmentEmployees", {
                departmentId: payload.departmentId,
                employeeId: id
              })
            );
          }
        }
        // Remove employees
        const employeeIdsDelete = currentEmployees.filter(
          (ce) => !payload.employeeIds.includes(ce.employee_id)
        );
        for (let index = 0; index < employeeIdsDelete.length; index++) {
          const element = employeeIdsDelete[index];
          promiseArray.push(dispatch("deleteDepartmentEmployees", element.id));
        }

        Promise.all(promiseArray).then(() => {
          resolve();
        });
      } catch (error) {
        return reject(error);
      }
    });
  },
  createDepartmentEmployees(
    { commit },
    payload: { departmentId: number; employeeId: number }
  ): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      departmentService
        .createDepartmentEmployee(payload.departmentId, payload.employeeId)
        .then((newDepartmentEmployee: DepartmentEmployee) => {
          commit("updateDepartmentEmployees", { departmentEmployees: [newDepartmentEmployee] });
          resolve(newDepartmentEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteDepartmentEmployees({ commit }, departmentEmployeesId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      departmentService
        .deleteDepartmentEmployee(departmentEmployeesId)
        .then((response) => {
          commit("deleteDepartmentEmployees", departmentEmployeesId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  DepartmentEmployeesByDepartmentId(
    { state },
    departmentId: number
  ): Promise<DepartmentEmployee[]> {
    return new Promise((resolve, reject) => {
      try {
        const departmentEmployees = state.departmentEmployees.filter(
          (de) => de.department_id === departmentId
        );
        return resolve(departmentEmployees);
      } catch (error) {
        return reject(error);
      }
    });
  }
};

export const mutations: MutationTree<IDepartmentState> = {
  reset(state: IDepartmentState): void {
    Object.assign(state, getInitialState());
  },
  deleteDepartment(state: IDepartmentState, departmentId: number): void {
    state.departments = state.departments.filter((existing) => existing.id !== departmentId);
  },
  updateDepartments(state: IDepartmentState, payload: { departments: Department[] }): void {
    state.departments = [
      ...payload.departments,
      ...state.departments.filter(
        (oldDepartments) =>
          !payload.departments.find((newDepartments) => newDepartments.equals(oldDepartments))
      )
    ];
  },
  updateDepartmentEmployees(
    state: IDepartmentState,
    payload: { departmentEmployees: DepartmentEmployee[] }
  ): void {
    state.departmentEmployees = [
      ...payload.departmentEmployees,
      ...state.departmentEmployees.filter(
        (oldDepartments) =>
          !payload.departmentEmployees.find((newDepartments) =>
            newDepartments.equals(oldDepartments)
          )
      )
    ];
  },
  deleteDepartmentEmployees(state: IDepartmentState, departmentEmployeesId: number): void {
    state.departmentEmployees = state.departmentEmployees.filter(
      (existing) => existing.id !== departmentEmployeesId
    );
  }
};

export const departmentsStoreModule: Module<IDepartmentState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
